import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { WorkflowForm, WorkflowFormComponent, WorkflowFormKey, WorkflowTaskDataService } from '@lib/workflow-integration';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    IPurchaseRequisitionTaskPreview,
    PurchaseRequisitionTaskPreview,
} from '../../types/PurchaseRequisitionTaskPreview';
import { DemandDataService } from '../../../demand/demand-data.service';
import { IPurchaseRequisitionTask, PurchaseRequisitionTask } from '../../types/PurchaseRequisitionTask';
import { ApproveDemandCompletionData } from './types/ApproveDemandCompletionData';
import {
    createDeeplinkCompletionContext,
    CustomRouter,
    DeeplinkCompletionAction,
    DeeplinkCompletionContextService,
    NotificationMessageLevel,
    NotificationService,
} from '@lib/core';
import { PurchaseRequisitionDetailComponent } from '../../../purchase-requisition/purchase-requisition-detail/purchase-requisition-detail.component';
import { ActivatedRoute } from '@angular/router';
import { CommentDataService } from '@lib/comment';
import { ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import { IExplanationDialogResult, ExplanationDialogService } from '@lib/util';
import { I18nService } from '@lib/i18n';
import { HttpErrorResponse } from '@angular/common/http';
import { DemandValidationError, IDemandValidationError } from '../../../demand/validation/types/IDemandValidationError';
import { ValidationSubject } from '../../../demand/validation/types/IValidationError';
import {
    AccountingValidationError,
    IAccountingValidationError,
} from '../../../demand/validation/types/IAccountingValidationError';
import { PurchaseRequisitionDataService } from '../../../purchase-requisition/purchase-requisition-data.service';
import { IPurchaseRequisitionCompanyCodeValidationResponse } from '../../../purchase-requisition/types/IPurchaseRequisitionCompanyCodeValidationResponse';
import { firstValueFrom } from 'rxjs';

@WorkflowForm(
    marker('Bedarfsmeldung freigeben'),
    [WorkflowFormKey.PURCHASE_REQUISITION_RELEASE_FORM],
    (dto: IPurchaseRequisitionTaskPreview, displayName: string) => new PurchaseRequisitionTaskPreview(dto, displayName),
    marker('Bedarfsmeldung freigeben'),
)
@Component({
    selector: 'approve-demand-task',
    templateUrl: './approve-demand-task.component.html',
    styleUrls: ['./approve-demand-task.component.scss'],
    standalone: false,
})
export class ApproveDemandTaskComponent extends WorkflowFormComponent implements OnInit {
    private readonly taskDataService = inject(WorkflowTaskDataService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly commentDataService = inject(CommentDataService);
    private readonly itemClassificationDataService = inject(ItemClassificationDataService);
    private readonly explanationDialog = inject(ExplanationDialogService);
    private readonly fileDataService = inject(FileDataService);
    private readonly router = inject(CustomRouter);
    private readonly route = inject(ActivatedRoute);
    private readonly i18n = inject(I18nService);
    private readonly deeplinkCompletionContext = inject(DeeplinkCompletionContextService);
    private readonly purchaseRequisitionDataService = inject(PurchaseRequisitionDataService);
    private readonly notificationService = inject(NotificationService);

    @ViewChild('purchaseRequisitionDetailComponent')
    private readonly purchaseRequisitionDetailComponent?: PurchaseRequisitionDetailComponent;
    public task?: PurchaseRequisitionTask;
    private hasCompanyCodeValidationError = false;

    /**
     * EPROC-11425 reopen #5b (238308): the demand-invalid branch of approvalBlocked carries no
     * hint of its own — the approver saw a dead release button and nothing else.
     */
    public get showDemandInvalidHint(): boolean {
        return !this.isCompleted && !this.purchaseRequisitionDetailComponent?.isValid;
    }

    public get approvalBlocked(): boolean {
        return !this.purchaseRequisitionDetailComponent?.isValid || this.isCompleted;
    }

    decide(isApproved: boolean): void {
        if (!this._isCompleted && (!isApproved || !this.approvalBlocked)) {
            if (isApproved) {
                this._isCompleted = true;
                this.complete(isApproved);
            } else {
                // EPROC-12154: lock the form only once the rejection is confirmed, a cancelled dialog keeps it usable
                this.explanationDialog.open().subscribe((result: IExplanationDialogResult) => {
                    if (!result.cancelled) {
                        this._isCompleted = true;
                        this.complete(isApproved, result.explanation);
                    }
                });
            }
        }
    }

    ngOnInit(): void {
        this.taskId = this.taskId ? this.taskId : this.route.snapshot.params['taskId'];
        this.getTask();
    }

    public getTask() {
        if (this.taskId) {
            this.taskDataService
                .getSingleTaskById<IPurchaseRequisitionTask>(this.taskId)
                .subscribe(
                    (task: IPurchaseRequisitionTask) =>
                        (this.task = new PurchaseRequisitionTask(
                            task,
                            this.demandDataService,
                            this.commentDataService,
                            this.itemClassificationDataService,
                            this.fileDataService,
                            this.i18n,
                        )),
                );
        }
    }

    private async complete(isApproved: boolean, explanation?: string): Promise<void> {
        if (this.task && (!isApproved || this.purchaseRequisitionDetailComponent?.isValid)) {
            await this.task.purchaseRequisition.demand.saveMetaData();
            if (isApproved) {
                const isCompanyCodeValid = await this.validateCompanyCode();
                if (!isCompanyCodeValid) {
                    this._isCompleted = false;
                    return;
                }
            }
            this.taskDataService
                .completeTask(this.task.id, new ApproveDemandCompletionData(isApproved, explanation))
                .subscribe({
                    next: () => {
                        this.deeplinkCompletionContext.completeRun(
                            createDeeplinkCompletionContext(
                                isApproved
                                    ? DeeplinkCompletionAction.PURCHASE_REQUISITION_APPROVED
                                    : DeeplinkCompletionAction.PURCHASE_REQUISITION_REJECTED,
                                this.task!.purchaseRequisition.number,
                            ),
                        );
                        this.completed.emit();
                        if (this.routeToListAfterCompletion) {
                            this.router.navigate([this.router.getViewFromRoute(), 'task', 'list'], {
                                replaceUrl: true,
                            });
                        }
                    },
                    error: (error: HttpErrorResponse) => {
                        this._isCompleted = false;

                        const validationError: IDemandValidationError | undefined = error?.error?.error;
                        if (
                            validationError &&
                            validationError.subject === ValidationSubject.DEMAND &&
                            this.task?.purchaseRequisition
                        ) {
                            this.task.purchaseRequisition.demand.setValidationError(
                                new DemandValidationError(validationError, this.i18n),
                            );
                        }
                    },
                });
        }
    }

    private async validateCompanyCode(): Promise<boolean> {
        if (!this.task?.purchaseRequisition?.id) {
            return true;
        }

        try {
            const response = await firstValueFrom(
                this.purchaseRequisitionDataService.validateCompanyCode(this.task.purchaseRequisition.id),
            );
            return this.handleCompanyCodeValidationResponse(response);
        } catch {
            this.notificationService.showNotification(
                marker('Kontierungsprüfung für den Buchungskreis konnte nicht durchgeführt werden.'),
                NotificationMessageLevel.ERROR,
            );
            return false;
        }
    }

    private handleCompanyCodeValidationResponse(response: IPurchaseRequisitionCompanyCodeValidationResponse): boolean {
        if (response.companyCodeMissing) {
            this.applyCompanyCodeMissingError();
            this.notificationService.showNotification(
                marker('Buchungskreis muss gesetzt sein, bevor die Bedarfsmeldung freigegeben werden kann.'),
                NotificationMessageLevel.ERROR,
            );
            return false;
        }

        if (!response.errors?.length) {
            this.clearCompanyCodeValidationError();
            return true;
        }

        const message = this.buildCompanyCodeValidationMessage(response.errors);
        if (response.blocking) {
            this.applyCompanyCodeValidationError(response.errors);
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR, undefined, true);
            return false;
        }

        this.notificationService.showNotification(message, NotificationMessageLevel.WARNING, undefined, true);
        return true;
    }

    private buildCompanyCodeValidationMessage(errors: IAccountingValidationError[]): string {
        return errors.map((error) => new AccountingValidationError(error, this.i18n).message).join('\n');
    }

    private applyCompanyCodeMissingError(): void {
        if (!this.task?.purchaseRequisition?.demand) {
            return;
        }

        const message = this.i18n.translateInstant(
            marker('Buchungskreis muss gesetzt sein, bevor die Bedarfsmeldung freigegeben werden kann.'),
        );
        const dto: IDemandValidationError = {
            demandId: this.task.purchaseRequisition.demand.id,
            otherErrors: true,
            otherErrorsMessage: message,
            extensionErrors: [],
            subject: ValidationSubject.DEMAND,
        };
        this.task.purchaseRequisition.demand.setValidationError(new DemandValidationError(dto, this.i18n));
        this.hasCompanyCodeValidationError = true;
    }

    private applyCompanyCodeValidationError(errors: IAccountingValidationError[]): void {
        if (!this.task?.purchaseRequisition?.demand) {
            return;
        }

        const dto: IDemandValidationError = {
            demandId: this.task.purchaseRequisition.demand.id,
            otherErrors: false,
            otherErrorsMessage: '',
            extensionErrors: errors,
            subject: ValidationSubject.DEMAND,
        };
        this.task.purchaseRequisition.demand.setValidationError(new DemandValidationError(dto, this.i18n));
        this.hasCompanyCodeValidationError = true;
    }

    private clearCompanyCodeValidationError(): void {
        if (this.hasCompanyCodeValidationError && this.task?.purchaseRequisition?.demand) {
            this.task.purchaseRequisition.demand.validationError = undefined;
            this.hasCompanyCodeValidationError = false;
        }
    }
}
